/**
 * VOL_SPIKE_TIMELINE — Volume Spike Timeline
 *
 * Only days where volume exceeds the n-period MA are plotted as bubbles.
 * Y-axis = percentage above the MA, so spikes can be compared directly to each
 * other across any zoom level.  Bubble radius grows with severity; colour deepens
 * from amber → orange → deep red along the same heat-ramp used by SpikeStrip.
 *
 * Non-spike days have result.pct = null → they are invisible and do not
 * affect y-axis auto-scaling, so the pane never overflows.
 */

import { IndicatorSeries, LineType, type IndicatorTemplate } from 'klinecharts';

// ── Heat-ramp colour helpers ──────────────────────────────────────────────────

type RGBA = [number, number, number, number];

function lerpRGBA(a: RGBA, b: RGBA, t: number): string {
  const r  = Math.round(a[0] + (b[0] - a[0]) * t);
  const g  = Math.round(a[1] + (b[1] - a[1]) * t);
  const bl = Math.round(a[2] + (b[2] - a[2]) * t);
  const al = +(a[3] + (b[3] - a[3]) * t).toFixed(2);
  return `rgba(${r},${g},${bl},${al})`;
}

const COLOR_STOPS: [number, RGBA][] = [
  [0,   [80,  80, 110, 0.40]],
  [50,  [255, 200,   0, 0.75]],
  [150, [255, 100,   0, 0.90]],
  [300, [239,  83,  80, 1.00]],
];

function spikeColor(pct: number): string {
  for (let s = 0; s < COLOR_STOPS.length - 1; s++) {
    const [p1, c1] = COLOR_STOPS[s];
    const [p2, c2] = COLOR_STOPS[s + 1];
    if (pct <= p2) {
      const t = Math.max(0, Math.min(1, (pct - p1) / (p2 - p1)));
      return lerpRGBA(c1, c2, t);
    }
  }
  return 'rgba(239,83,80,1.00)';
}

// ── Internal types ────────────────────────────────────────────────────────────

type KBar = { timestamp: number; volume?: number };

export type SpikeTimelineRow = { pct: number | null };

type DrawParams = {
  ctx:          CanvasRenderingContext2D;
  visibleRange: { from: number; to: number };
  bounding:     { width: number; height: number; left: number; top: number; right: number; bottom: number };
  xAxis:        { convertToPixel: (i: number) => number };
  yAxis:        { convertToPixel: (v: number) => number };
  indicator:    { result: SpikeTimelineRow[] };
};

// ── Indicator template ────────────────────────────────────────────────────────

const volSpikeTimeline: IndicatorTemplate<SpikeTimelineRow> = {
  name:       'VOL_SPIKE_TIMELINE',
  shortName:  'SpikeTL',
  series:     IndicatorSeries.Normal,
  shouldOhlc: false,

  // 'pct' drives the y-axis and bubble y-placement. null on non-spike days.
  // ChartLegend derives vol (bar data) and ma = vol/(1+pct/100) without extra figures.
  figures: [
    { key: 'pct', title: '\u0394%: ', type: 'line' },
  ],

  styles: {
    lines: [
      { style: LineType.Solid, size: 0, color: '#60A5FA', smooth: false, dashedValue: [2, 2] },
    ],
  },

  calc: (dataList, indicator) => {
    const params = (indicator as { calcParams?: number[] }).calcParams;
    const maLen  = Math.max(1, Math.floor(params?.[0] ?? 20));
    const bars   = dataList as unknown as KBar[];

    return bars.map((_, i) => {
      const vol = bars[i].volume ?? null;
      if (vol == null || i < maLen - 1) return { pct: null };

      let sum = 0;
      for (let j = i - maLen + 1; j <= i; j++) {
        const v = bars[j].volume;
        if (v == null) return { pct: null };
        sum += v;
      }
      const ma  = sum / maLen;
      if (ma <= 0) return { pct: null };

      const pct = ((vol - ma) / ma) * 100;
      // Only spike days (volume strictly above MA) are plotted
      return { pct: pct > 0 ? pct : null };
    });
  },

  draw: (params: unknown): boolean => {
    const { ctx, visibleRange, bounding, xAxis, yAxis, indicator } =
      params as DrawParams;

    const results = indicator.result;
    const { from, to } = visibleRange;
    if (to <= from) return true;

    ctx.save();

    // ── Dashed zero-baseline ("at MA" reference line) ─────────────────────
    // ctx is translated by klinecharts so y=0 is the pane content top and
    // y=bounding.height is the pane content bottom.
    try {
      const y0   = yAxis.convertToPixel(0);
      const xL   = xAxis.convertToPixel(from);
      const xR   = xAxis.convertToPixel(to);
      if (y0 >= 0 && y0 <= bounding.height) {
        ctx.beginPath();
        ctx.setLineDash([4, 4]);
        ctx.strokeStyle = 'rgba(150,150,180,0.35)';
        ctx.lineWidth   = 1;
        ctx.moveTo(xL, y0);
        ctx.lineTo(xR, y0);
        ctx.stroke();
      }
    } catch { /* y-axis not ready — skip baseline */ }
    ctx.setLineDash([]);

    // ── Spike bubbles ─────────────────────────────────────────────────────
    for (let i = from; i <= to; i++) {
      const r = results[i];
      if (!r || r.pct == null) continue;

      const pct    = r.pct;
      const x      = xAxis.convertToPixel(i);
      const y      = yAxis.convertToPixel(pct);
      // Radius: 4 px minimum, grows with √pct, capped at 22 px
      const radius = Math.min(4 + Math.sqrt(pct) * 1.2, 22);
      const fill   = spikeColor(pct);

      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fillStyle   = fill;
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.22)';
      ctx.lineWidth   = 1;
      ctx.stroke();
    }

    ctx.restore();
    return true; // suppress klinecharts' default line rendering
  },
};

export default volSpikeTimeline;
